import {
  Spacing
} from "./chunk-E5DUKMCU.js";
import {
  Button
} from "./chunk-4EXNT44R.js";
import {
  UnstyledButton
} from "./chunk-I5WDNJY3.js";
import {
  z
} from "./chunk-55OQPFLF.js";
import {
  Icon
} from "./chunk-RMZQSXO2.js";
import {
  standardFocusOutline
} from "./chunk-RNCUEVKR.js";
import {
  colors
} from "./chunk-HO7LVJ3R.js";

// src/components/Drawer.tsx
import styled from "@emotion/styled";
import { useRef, useState } from "react";
import { Fragment, jsx, jsxs } from "@emotion/react/jsx-runtime";
var Drawer = (props) => {
  const [isOpen, setIsOpen] = useState(true);
  const [lastY, setLastY] = useState(null);
  const [totalDeltaY, setTotalDeltaY] = useState(0);
  const [fractionVisible, setFractionVisible] = useState(1);
  const [grabbing, setGrabbing] = useState(false);
  const drawerContainerRef = useRef(null);
  const handleClose = () => {
    if (props.nonDismissable) {
      return;
    }
    setIsOpen(false);
    setTimeout(() => {
      props.onClose && props.onClose();
      setTotalDeltaY(0);
      setLastY(null);
    }, 300);
  };
  const handleTouchMove = (event) => {
    var _a, _b;
    if (props.nonDismissable) {
      return;
    }
    if (lastY === null) {
      setLastY(event.touches[0].clientY);
    } else {
      const deltaY = event.touches[0].clientY - lastY;
      const topOfDrawer = (((_a = drawerContainerRef.current) == null ? void 0 : _a.getBoundingClientRect().top) || 0) + window.scrollY;
      const drawerContainerHeight = ((_b = drawerContainerRef.current) == null ? void 0 : _b.getBoundingClientRect().height) || 1;
      const newFractionVisible = Math.min(
        1,
        (window.innerHeight - topOfDrawer) / drawerContainerHeight
      );
      setFractionVisible(newFractionVisible);
      setLastY(event.touches[0].clientY);
      setTotalDeltaY((prev) => Math.max(prev + deltaY, 0));
    }
  };
  const handleTouchStart = () => {
    if (props.nonDismissable) {
      return;
    }
    setGrabbing(true);
  };
  const handleTouchEnd = () => {
    if (props.nonDismissable) {
      return;
    }
    setGrabbing(false);
    if (fractionVisible < 0.8) {
      handleClose();
    }
    setTotalDeltaY(0);
    setLastY(null);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Background,
      {
        isOpen,
        fractionVisible,
        onClick: handleClose
      }
    ),
    /* @__PURE__ */ jsx(
      DrawerContainer,
      {
        isOpen,
        onTouchMove: handleTouchMove,
        onTouchStart: handleTouchStart,
        onTouchEnd: handleTouchEnd,
        totalDeltaY,
        grabbing,
        ref: drawerContainerRef,
        children: /* @__PURE__ */ jsxs(DrawerInnerContainer, { kind: props.kind, padding: props.padding, children: [
          props.grabber && !props.nonDismissable && /* @__PURE__ */ jsx(Grabber, { onClick: handleClose, children: /* @__PURE__ */ jsx(GrabberBar, {}) }),
          props.handleBack && /* @__PURE__ */ jsx(BackButtonContainer, { children: /* @__PURE__ */ jsx(
            Button,
            {
              onClick: props.handleBack,
              icon: { name: "ChevronLeft" },
              kind: "ghost",
              size: "Small"
            }
          ) }),
          props.closeButton && !props.nonDismissable && /* @__PURE__ */ jsx(CloseButtonContainer, { children: /* @__PURE__ */ jsx(CloseButton, { onClick: handleClose, type: "button", children: /* @__PURE__ */ jsx(Icon, { name: "Close", width: 10 }) }) }),
          props.children
        ] })
      }
    )
  ] });
};
var Background = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: ${z.modalOverlay};

  // Animate opacity
  transition: opacity 0.3s ease-in-out;
  opacity: ${(props) => props.isOpen ? props.fractionVisible : "0"};

  animation: open 0.3s ease-in-out;

  @keyframes open {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;
var DrawerContainer = styled.div`
  position: fixed;
  max-height: 100dvh;
  width: 100%;
  right: 0;
  bottom: 0;
  transform: translateY(${(props) => `${props.totalDeltaY}px`});
  z-index: ${z.modalContainer};
  display: flex;
  flex-direction: column;
  align-items: center;

  // Only smooth transition when not grabbing, otherwise dragging will feel very laggy
  ${(props) => props.grabbing && props.isOpen ? "" : "transition: transform 0.3s ease-in-out;"};

  // Animate the drawer opening and closing, and make sure the the drawer stays closed.
  animation: 0.3s ease-in-out
    ${(props) => props.isOpen ? "openDrawer" : "closeDrawer forwards"};

  @keyframes openDrawer {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0%);
    }
  }

  @keyframes closeDrawer {
    from {
      transform: translateY(${(props) => `${props.totalDeltaY}px`});
    }
    to {
      transform: translateY(100%);
    }
  }
`;
var DrawerInnerContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: ${(props) => props.kind === "floating" ? `calc(100% - ${Spacing.md * 2}px)` : "100%"};
  min-width: 320px;
  height: 100%;
  ${(props) => props.kind === "floating" ? `bottom: ${Spacing.px.md};` : ""}
  border-radius: ${(props) => props.kind === "floating" ? Spacing.px.lg : `${Spacing.px.lg} ${Spacing.px.lg} 0 0`};
  background-color: ${({ theme }) => theme.bg};
  padding: ${(props) => props.padding ? `${props.padding}` : `${Spacing.px["6xl"]} ${Spacing.px.xl} ${Spacing.px["2xl"]}
    ${Spacing.px.xl}`};
  overflow-y: auto;
`;
var Grabber = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${Spacing.px.lg};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 9px;
  cursor: pointer;
`;
var CloseButtonContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  border-radius: 50%;
  background-color: ${colors.grayBlue94};
  padding: ${Spacing.px.xs};
  height: 30px;
  width: 30px;
  display: flex;
  align-items: center;

  * > * {
    line-height: 14px;
  }
`;
var CloseButton = styled(UnstyledButton)`
  ${standardFocusOutline}
  width: 24px;
  height: 24px;
  justify-self: flex-end;
`;
var BackButtonContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  border-radius: 50%;
  padding: ${Spacing.px.xs};
  height: 30px;
  width: 30px;
  display: flex;
  align-items: center;
`;
var GrabberBar = styled.div`
  width: 36px;
  height: 5px;
  border-radius: 2.5px;
  background: #c0c9d6;
`;

export {
  Drawer
};
